import EventEmitter from 'event-emitter';
import IO from 'socket.io-client';
import { PushStream } from './pushstream';
import log from 'loglevel';
import { pageSession, toUint8ArrayExt, decompressData, safeJsonParse } from '../utils/utils';
import { toUint8Array, decode } from 'js-base64';
import Tim from '../message/tim';
import EVENT from '../init/event_enum.js';

export default class Adapter {
  constructor(params, msgIns) {
    EventEmitter(this);
    if (typeof WebSocket !== 'undefined' || typeof MozWebSocket !== 'undefined' || typeof document === 'undefined') {
      this.supportWS = true;
    }
    this._msgIns = msgIns; // 消息sdk interfaces 实例
    this.channelMap = new Map(); // 存储加入房间的频道的信息
    this.opt = params || {};
    this.opt.accountId = this.opt.accountId || this.opt.third_party_user_id;
    this.opt.third_party_user_id = this.opt.accountId;
    if (this.opt.context && typeof this.opt.context !== 'string') {
      this.opt.context = JSON.stringify(this.opt.context);
    }
    this.reconnectionAttempts = Infinity; // 重连尝试次数（设置无限）
    this.reconnectionDelayMax = 8000; // 重新连接之间等待的最长时间（单位: 毫秒）
    this._ps = null; // PushStream创建的wss链接实例
    this._ws = null; // socket.io-client创建的wss链接实例

    this.online = false;
    this.lastPing = null; // 上一次 this._ws 收到包的时间
    this.lastPingCheckTime = 1000 * 30; // 两次收到包的时间差限制

    // 腾讯im链接信息
    this.opt.timConnectionInfo = this.opt.extra_connection_info?.tim_connection_info?.sdk_app_id
      ? this.opt.extra_connection_info.tim_connection_info
      : null;
    this._tim = null; // tim创建的链接实例
  }

  /**
   * 初始化socket
   * @param {string} server socket链接地址
   * @param {string} token socket连接token
   */
  async connect() {
    const { channelId, socket_server, connection_token, nginx_server, timConnectionInfo } = this.opt;
    if (timConnectionInfo) {
      // 开启了 TIM 支持
      this._tim = new Tim(timConnectionInfo, this._msgIns);
      this._timAddEventListener();
      this._initSocket(socket_server, connection_token, channelId);
      this.join(channelId);
      const wsPromise = new Promise((resolve, reject) => {
        this._ws.once('joined', resolve);
        setTimeout(() => reject(new Error('join timeout')), 10 * 1000);

        this.on('offline', this._onOffline.bind(this));
        this.on('online', this._onOnline.bind(this));
      });
      const timPromise = new Promise((resolve, reject) => {
        this._tim.once(EVENT.TIM_JOIN_GROUP_SUCCESS, resolve);
        setTimeout(() => reject(new Error('tim init timeout')), 8 * 1000);
      });
      const [wsResult, timResult] = await Promise.allSettled([wsPromise, timPromise]);
      // console.log('wsResult:', wsResult);
      // console.log('timResult:', timResult);
      if (timResult.status === 'rejected') {
        this._msgIns.emit(EVENT.TIM_INIT_FAILED);
      }
      if (wsResult.status === 'rejected' || timResult.status === 'rejected') {
        return Promise.reject(wsResult.reason);
      } else {
        return Promise.resolve(wsResult.value);
      }
    } else {
      // 未开启 TIM ，使用原来的逻辑
      this._initPushStream(nginx_server, channelId);
      this._initSocket(socket_server, connection_token, channelId);
      this.join(channelId);
      return new Promise((resolve, reject) => {
        this._ws.once('joined', resolve);
        setTimeout(() => reject(new Error('join timeout')), 10 * 1000);

        this.on('offline', this._onOffline.bind(this));
        this.on('online', this._onOnline.bind(this));
      });
    }
  }

  /**
   * 初始化socket (command 通道)
   * @param {string} server socket链接地址
   * @param {string} token socket连接token
   */
  _initSocket(server, token, channelId) {
    const transports = this.supportWS ? ['websocket', 'polling'] : ['longpolling'];
    const opt = {
      query: `token=${token}&ver=1.1.2&s=${pageSession}`,
      reconnectionAttempts: this.reconnectionAttempts,
      reconnectionDelayMax: this.reconnectionDelayMax,
      path: '/socket.io',
      rememberUpgrade: true,
      autoConnect: true,
      forceNew: true,
      transports
    };

    const socket = IO.connect(server, opt);
    // save sid, 注意！断线重连sid会变！！

    // 所有socket状态接收事件监听都是非广播的，也就说不管是两个浏览器，还是一个浏览器的两个tab,只能自己的wss可以收到
    // socket事件监听：
    // 开始连接
    socket.on('connecting', () => log.info('[socket] event: connecting'));
    // 已经连接
    socket.on('connect', () => log.info('[socket] event: connect'));
    // 连接建立成功
    socket.on('connected', () => log.info('[socket] event: connected'));
    // 上线成功（连接建立成功，状态维护处理完成）
    socket.on('joined', msg => {
      log.info('[socket] event: joined:', msg);
      this._onJoined(0, msg);
    });
    // 连接断开
    socket.on('disconnect', reason => {
      log.info('[socket] event: disconnect');
      this.emit('disconnect', reason);
    });
    // 尝试重连
    socket.on('reconnecting', attempt => {
      log.info('[socket] event: reconnecting');
      this._onConnectStatChange(0, 'reconnecting', attempt);
    });
    // 重连尝试，返回尝试次数
    socket.on('reconnect_attempt', attempt => {
      log.info('[socket] event: reconnect_attempt, attempt:', attempt);
      this.emit('reconnectAttempt', attempt);
    });
    // 重连成功
    socket.on('reconnect', attempt => {
      log.info('[socket] event: reconnect, attempt:', attempt);
      this._onReconnect(0, attempt);
    });
    // 消息数据包
    socket.io.on('packet', packet => {
      log.info('[socket] event: packet, packet:', packet);
      this.lastPingCheckTime = Date.now();
    });
    // socket其它错误，从源码看连接超时的时候会触发，但是默认连接超时时间是20s，而尝试重连最大间隔是8s左右，每次重连错误会把超时定时器重置，所以该事件永远不会触发
    /* eslint handle-callback-err: "off" */
    socket.on('error', error => null);

    // socket消息接收事件监听（广播消息）
    socket.on('msg', msg => {
      log.info('[socket] event: msg, msg:', msg);
      this._ondata(0, msg);
    });

    // 请求建立链接
    socket.connect();
    this._ws = socket;
  }

  /**
   * 初始化pushStream (chat message 通道)
   * @param {string} server pushStream连接地址
   */
  _initPushStream(server, channelId) {
    const pushStream = new PushStream({
      host: (server || '').replace('https://', '').replace('http://', ''), // 消息服务器域名
      modes: 'websocket',
      messagesPublishedAfter: 0, // 加载n秒内的历史消息，最大条数由服务器控制
      useSSL: true,
      messagesControlByArgument: true,
      autoReconnect: true // 自动重新连接
    });
    this._ps = pushStream;

    pushStream.onmessage = msg => this._ondata(1, msg);

    pushStream.onerror = error => {
      if (error) {
        if (error.type === 'load') {
          this._onDisconnect(1, error);
          return;
        } else if (error.type === 'timeout') {
          // 长轮训超时，不用理会
          return;
        }
      }
      log.error(`[pushStream] onerror：${JSON.stringify(error)}`);
    };

    pushStream.onstatuschange = event => {
      if (!event) return;
      log.info(`[pushStream] onstatuschage status:${JSON.stringify(event)}`);
      switch (event) {
        case PushStream.CLOSED:
          // entering disconnect
          break;
        case PushStream.CONNECTING:
          break;
        case PushStream.OPEN:
          break;
      }
    };

    pushStream.addChannel(channelId);
    pushStream.connect();
    log.info('[pushStream] connect success');
  }

  /**
   * 加入房间
   */
  join(channelId, opt, callback) {
    if (this.opt.timConnectionInfo) {
      if (this._tim) {
        this._tim.initialize();
      }
    } else {
      // if (this.channelMap.get(channelId)) return;
      if (!this._ps.channels[channelId]) {
        this._ps.addChannel(channelId);
      }
    }

    this._emitwsjoin(channelId);
    this._addChannelHandler(channelId, opt, this.opt);
    if (callback) {
      this._ws && this._ws.once('joined', callback);
    }
  }

  /**
   * 离开房间
   * @param {string} channelId channel频道ID
   */
  leave(channelId) {
    const opt = this.channelMap.get(channelId) || {};
    const third_party_user_id = opt.third_party_user_id || this.opt.third_party_user_id;
    if (this._ps) this._ps.removeChannel(channelId);
    if (this._ws) this._ws.emit('leave', { channel: channelId, third_party_user_id });
    this.channelMap.delete(channelId);
    log.info(`leave channelId:${channelId} , third_party_user_id:${third_party_user_id}`);
  }

  disconnect() {
    if (this._ws) this._ws.disconnect();
    if (this._ps) this._ps.disconnect();
    if (this._tim) this._tim.disconnect();
    log.info(`pushStream and Socket and tim disconnect`);
  }

  hasChannelCount() {
    return this.channelMap.size;
  }

  _emitwsjoin(channelId) {
    const opt = this.channelMap.get(channelId) || this.opt;
    const { third_party_user_id, context, hide } = opt;
    const joinData = { channel: channelId, third_party_user_id, context: context, hide };
    if (this._ws) {
      // 向服务端发送join事件，最终会调用node_modules/engine.io-client/lib/socket.js=>this.sendPacket('message', msg, options, fn);
      // msg是转换之后的数据（字符串），格式: 2["join",{"channel":"ch_510ad20f","third_party_user_id":"zx"}]
      // 服务端处理后会触发 joined 事件监听
      this._ws.emit('join', joinData);
    }
    /* prettier-ignore */
    log.info(`join channelId:${channelId} , third_party_user_id:${third_party_user_id} , other:${JSON.stringify({ context, hide })}`);
  }

  _addChannelHandler(channelId, opt) {
    const { context, third_party_user_id, hide } = opt || this.opt;
    const info = {
      channel: channelId,
      third_party_user_id: third_party_user_id,
      accountId: third_party_user_id,
      context: context,
      hide: hide
    };
    this.channelMap.set(channelId, info);
  }

  // begin 重连
  // 重连成功
  _onReconnect(ch, attempt) {
    log.warn(`socket reconnected attempt: ${attempt}`);
    // 加入最后加入 channel
    const firstChannelId = this.opt.channelId;
    this._emitwsjoin(firstChannelId);
    this.maybeCheckWs();
    this._onConnectStatChange(ch, 'reconnect');

    if (this._ws) {
      // 恢复之前曾经加入的 channel
      for (const [channel, info] of this.channelMap) {
        if (channel === firstChannelId) continue;
        const opt = info || this.opt;
        this._ws.emit('join', {
          channel: channel,
          third_party_user_id: opt.third_party_user_id,
          context: opt.context,
          hide: opt.hide
        });
      }
    }
    // PushStream 没有链接状态，所以不需要处理
  }

  _onDisconnect(ch, error) {
    //
  }

  _onJoined(ch) {
    this.online = true;
    this.emit('online');
  }

  _onConnectStatChange(ch, type) {
    if (type === 'reconnecting') {
      if (this.online) {
        this.online = false;
        this.emit('offline');
      }
      this.maybeCheckWs();
    }
  }

  _onOnline() {
    if (this._ps) {
      if (!this._ps.autoReconnect) {
        this._ps.autoReconnect = true;
        this._ps._reconnect();
      }
    }
  }

  _onOffline() {
    if (this._ps) {
      // 暂停 push 渠道的连接重试
      this._ps.autoReconnect = false;
    }
  }

  _onReconnectAttempt(attempt) {}

  // 可能需要检查连接是否正常
  async maybeCheckWs(force) {
    if (!this._ws) return;
    if (force) {
      // force 状态下加快检测速度
      const engine = this._ws.io && this._ws.io.engine;
      if (engine) {
        const probeTimeout = 1000 * 3;
        const currTimeout = engine.pingTimeout;
        const currInterval = engine.pingInterval;
        engine.pingTimeout = probeTimeout;
        engine.pingInterval = probeTimeout;
        engine.setPing();
        await new Promise(resolve => setTimeout(resolve, probeTimeout * 2));
        if (this._ws) {
          engine.pingTimeout = currTimeout;
          engine.pingInterval = currInterval;
        }
      }
    } else if (this.lastPing && Date.now() - this.lastPing > this.lastPingCheckTime) {
      if (this._ws.io && this._ws.io.engine) this._ws.io.engine.setPing();
    }
  }
  // endbegin 重连

  async _ondata(flag, data) {
    if (flag == 1) {
      log.info(`[pushStream] onmessage data:${data}`);
    } else if (flag == 2) {
      log.info(`[tim] onmessage data:${data}`);
    } else {
      log.info(`[socket] onmessage data:${JSON.stringify(data)}`);
    }
    let msg = await this._parseData(data);
    // 空数据不处理
    if (!msg) return;
    // 不是数组转成数组
    if (!Array.isArray(msg)) msg = [msg];
    // 循环发送
    for (let item of msg) {
      let c = safeJsonParse(item);
      if (Array.isArray(c)) {
        // 说明是二维数组
        this.dispatch(safeJsonParse(c[c.length - 1]));
      } else {
        this.dispatch(c);
      }
    }
  }

  _timAddEventListener() {
    if (!this._tim) return;
    this._tim.on('onTimCreateFailed', () => {
      log.info('[tim] ===onTimCreateFailed===:');
      // TIM 8s之内的初始化失败，自动重试
      if (Date.now() - this._tim.createTime < 8000) {
        setTimeout(
          () => {
            this._tim.initialize();
          },
          this._tim.retryCount > 0 ? this._tim.retryCount * 200 : 200
        );
      }
    });
    this._tim.on('onTimJoinGroupFailed', () => {
      log.info('[tim] ===onTimJoinGroupFailed===:');
      // TIM 8s之内的初始化失败，自动重试
      if (Date.now() - this._tim.createTime < 8000) {
        setTimeout(
          () => {
            this._tim.initialize();
          },
          this._tim.retryCount > 0 ? this._tim.retryCount * 200 : 200
        );
      }
    });
    this._tim.on('onMessageReceived', msg => this._ondata(2, msg));
  }

  _dirMsgFilter(user_ids, accountId) {
    if (typeof user_ids !== 'string') return 1;
    user_ids = user_ids.split(',');
    if (!Array.isArray(user_ids)) return 0;
    // 定向消息过滤判断开始
    if (!user_ids.length) return -1;
    // 在定向列表中
    if (user_ids.indexOf(accountId + '') >= 0) return 2;
    return -1;
  }

  dispatch(data) {
    if (!data) return;
    // 过滤定向广播的消息
    if (this._dirMsgFilter(data.user_ids, this.opt.third_party_user_id) <= 0) return;
    // 派发消息数据
    this.emit('service_message', data);
  }

  /**
   * 解析数据
   * @param {object} msg 被解析得数据
   */
  async _parseData(msg) {
    msg = safeJsonParse(msg);
    // 有 comp_alg 字段则按照新消息格式处理，否则按照老消息格式处理；新消息有可能有多条压缩的，老消息都是单条的
    const compAlg = msg?.comp_alg; // 压缩算法，n:无压缩 gz:gzip压缩
    // 老消息直接返回
    if (!compAlg) return msg;

    // 以下是新消息格式处理
    const encoding = msg.encoding; // 内容编码格式，text：原始文本 b64：base64编码常规编码 b64_2：base64编码二进制编码
    let decodeContent;
    try {
      if (encoding === 'b64') {
        decodeContent = decode(msg.content); // base64常规解码
      } else if (encoding === 'b64_2') {
        decodeContent = toUint8Array(msg.content); // base64二进制编码解码
      } else {
        decodeContent = msg.content;
      }
    } catch (ex) {
      // 解密出现错误
      if (ex && ex.message.indexOf('TypedArray.from requires') > -1) {
        decodeContent = toUint8ArrayExt(msg.content);
      }
    }

    let result;
    if (decodeContent) {
      if (compAlg === 'gz') {
        let originalContentStr;
        try {
          // 当前只支持gzip压缩
          const u8sData = await decompressData(decodeContent); // gunzip 解压缩，必须是b64_2解码数据,不是则说明服务端数据处理有问题
          originalContentStr = new TextDecoder().decode(u8sData); // 将Uint8Array转回字符串
        } catch (ex) {
          log.error('gunzip data error:', ex);
        }
        // 转json对象
        result = safeJsonParse(originalContentStr);
      } else {
        // 其它的压缩方式暂不处理
        result = safeJsonParse(decodeContent);
      }
    }
    return result;
  }
}
